import React, { useState, useEffect, useCallback } from 'react';
import { Page, Grid, Card, Table, Button, Form, Badge, Text } from 'tabler-react';
import SiteWrapper from '../SiteWrapper';
import * as measureService from '../store/services/measureServices';

function MeasureLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sessionFilter, setSessionFilter] = useState('');
    const [eventTypeFilter, setEventTypeFilter] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(false);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (sessionFilter) params.append('session_id', sessionFilter);
            if (eventTypeFilter) params.append('event_type', eventTypeFilter);
            params.append('limit', 100);

            const response = await measureService.getMeasureLogs(params.toString());
            // Laravel returns { status: 200, data: [...] }
            if (response.data?.status === 200) {
                const fetchedLogs = response.data.data || [];
                console.log('Fetched logs count:', fetchedLogs.length);
                console.log('Event types present:', [...new Set(fetchedLogs.map(l => l.event_type))]);
                console.log('Sample log:', fetchedLogs[0]);
                setLogs(fetchedLogs);
            } else {
                setLogs([]);
            }
        } catch (error) {
            console.error('Failed to fetch logs:', error);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [sessionFilter, eventTypeFilter]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(fetchLogs, 3000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh, fetchLogs]);

    const downloadLogs = () => {
        const dataStr = JSON.stringify(logs, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `measure-logs-${new Date().toISOString()}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const deleteAllLogs = async () => {
        if (!window.confirm('Are you sure you want to delete ALL logs? This cannot be undone.')) {
            return;
        }
        try {
            await measureService.deleteAllLogs();
            setLogs([]);
            alert('All logs deleted successfully');
        } catch (error) {
            console.error('Failed to delete logs:', error);
            alert('Failed to delete logs');
        }
    };

    const getEventColor = (eventType) => {
        if (eventType === 'SESSION_START') return 'success';
        if (eventType === 'SESSION_END') return 'secondary';
        if (eventType === 'MODAL_SHOWN') return 'danger';
        if (eventType === 'VALIDATION_BLOCKED') return 'warning';
        if (eventType === 'SESSION_TRANSITION') return 'info';
        if (eventType === 'TIMER_START') return 'cyan';
        if (eventType === 'TIMER_END') return 'azure';
        if (eventType === 'PHOTO_CAPTURE_START') return 'purple';
        if (eventType === 'PHOTO_CAPTURED') return 'purple';
        if (eventType === 'PHOTO_CAPTURED_GROUP') return 'purple';
        if (eventType === 'PHOTO_CAPTURE_COMPLETE') return 'purple';
        if (eventType === 'PHOTO_CAPTURE_CANCELLED') return 'orange';
        if (eventType === 'UPLOAD_START') return 'blue';
        if (eventType === 'UPLOAD_SUCCESS') return 'green';
        if (eventType === 'UPLOAD_ERROR') return 'red';
        if (eventType === 'UPLOAD_TIMEOUT') return 'red';
        if (eventType === 'UPLOAD_NETWORK_ERROR') return 'red';
        if (eventType === 'UPLOAD_AUTH_EXPIRED') return 'orange';
        if (eventType === 'UPLOAD_EXCEPTION') return 'red';
        return 'primary';
    };

    const getEventLabel = (eventType) => {
        // Handle missing/null event types
        if (!eventType || eventType === 'UNKNOWN') {
            return 'Unknown Event';
        }

        const labels = {
            'SESSION_START': 'Session Start',
            'SESSION_END': 'Session End',
            'MODAL_SHOWN': 'Modal Shown',
            'VALIDATION_BLOCKED': 'Validation Blocked',
            'SESSION_TRANSITION': 'Session Transition',
            'TIMER_START': 'Timer Start',
            'TIMER_END': 'Timer End',
            'PHOTO_CAPTURE_START': 'Photo Capture Start',
            'PHOTO_CAPTURED': 'Photo Captured',
            'PHOTO_CAPTURED_GROUP': 'Photos Captured',
            'PHOTO_CAPTURE_COMPLETE': 'Photo Capture Complete',
            'PHOTO_CAPTURE_CANCELLED': 'Photo Capture Cancelled',
            'UPLOAD_START': 'Upload Start',
            'UPLOAD_SUCCESS': 'Upload Success',
            'UPLOAD_ERROR': 'Upload Error',
            'UPLOAD_TIMEOUT': 'Upload Timeout',
            'UPLOAD_NETWORK_ERROR': 'Upload Network Error',
            'UPLOAD_AUTH_EXPIRED': 'Auth Expired',
            'UPLOAD_EXCEPTION': 'Upload Exception',
            'POSE_DETECTED': 'Pose Detected',
            'POSE_CHANGED': 'Pose Changed'
        };
        return labels[eventType] || eventType;
    };

    const getSummaryText = (log) => {
        const data = log.data || {};

        if (log.event_type === 'PHOTO_CAPTURED_GROUP') {
            return `${data.totalPhotos} photos captured`;
        }
        if (log.event_type === 'PHOTO_CAPTURED') {
            return `Photo ${data.photoNumber}/${data.totalPhotos}`;
        }
        if (log.event_type === 'TIMER_START' || log.event_type === 'TIMER_END') {
            return data.type || 'Timer event';
        }
        if (log.event_type === 'UPLOAD_SUCCESS') {
            return `${data.photoCount} photo(s) uploaded`;
        }
        if (log.event_type === 'UPLOAD_ERROR') {
            return `Upload failed: ${data.status}`;
        }
        if (log.event_type === 'VALIDATION_BLOCKED') {
            return data.reason || 'Validation blocked';
        }
        if (log.event_type === 'MODAL_SHOWN') {
            return data.type || 'Modal shown';
        }

        return data.type || data.measureType || 'View data';
    };

    // Group logs by session and consolidate photo capture events
    const groupedBySessions = logs.reduce((acc, log) => {
        const key = log.measurement_flow_id || log.session_id;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(log);
        return acc;
    }, {});

    // Post-process to group consecutive PHOTO_CAPTURED events
    const consolidatedSessions = {};
    Object.keys(groupedBySessions).forEach(sessionId => {
        const sessionLogs = groupedBySessions[sessionId];

        // Sort by timestamp_ms in descending order (newest first)
        const sortedLogs = [...sessionLogs].sort((a, b) => {
            const timeA = a.event_timestamp_ms || new Date(a.event_timestamp).getTime();
            const timeB = b.event_timestamp_ms || new Date(b.event_timestamp).getTime();
            return timeB - timeA; // Reversed for newest first
        });

        const consolidated = [];
        let photoCaptureGroup = [];

        sortedLogs.forEach((log, index) => {
            if (log.event_type === 'PHOTO_CAPTURED') {
                photoCaptureGroup.push(log);

                // If next log is not PHOTO_CAPTURED or this is the last log, consolidate
                const nextLog = sortedLogs[index + 1];
                if (!nextLog || nextLog.event_type !== 'PHOTO_CAPTURED') {
                    if (photoCaptureGroup.length > 1) {
                        // Create a consolidated log entry
                        consolidated.push({
                            ...photoCaptureGroup[0],
                            id: `photo-group-${photoCaptureGroup[0].id}`,
                            event_type: 'PHOTO_CAPTURED_GROUP',
                            isGroup: true,
                            groupedLogs: photoCaptureGroup,
                            data: {
                                ...photoCaptureGroup[0].data,
                                totalPhotos: photoCaptureGroup.length,
                                photos: photoCaptureGroup.map(p => ({
                                    photoNumber: p.data?.photoNumber,
                                    filePath: p.data?.filePath
                                }))
                            }
                        });
                    } else if (photoCaptureGroup.length === 1) {
                        consolidated.push(photoCaptureGroup[0]);
                    }
                    photoCaptureGroup = [];
                }
            } else {
                consolidated.push(log);
            }
        });

        consolidatedSessions[sessionId] = consolidated;
    });

    return (
        <SiteWrapper>
            <Page.Content title="Measure Logs" subTitle="Live stream of measurement session events">
                <Grid.Row>
                    <Grid.Col md={12}>
                        <Card>
                            <Card.Body>
                                <Grid.Row>
                                    <Grid.Col md={3}>
                                        <Form.Group label="Session ID">
                                            <Form.Input
                                                placeholder="Filter by session..."
                                                value={sessionFilter}
                                                onChange={(e) => setSessionFilter(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Grid.Col>
                                    <Grid.Col md={3}>
                                        <Form.Group label="Event Type">
                                            <Form.Select
                                                value={eventTypeFilter}
                                                onChange={(e) => setEventTypeFilter(e.target.value)}
                                            >
                                                <option value="">All Events</option>
                                                <option value="SESSION_START">Session Start</option>
                                                <option value="SESSION_END">Session End</option>
                                                <option value="SESSION_TRANSITION">Session Transition</option>
                                                <option value="MODAL_SHOWN">Modal Shown</option>
                                                <option value="VALIDATION_BLOCKED">Validation Blocked</option>
                                                <option value="TIMER_START">Timer Start</option>
                                                <option value="TIMER_END">Timer End</option>
                                                <option value="PHOTO_CAPTURE_START">Photo Capture Start</option>
                                                <option value="PHOTO_CAPTURED">Photo Captured</option>
                                                <option value="PHOTO_CAPTURE_COMPLETE">Photo Capture Complete</option>
                                                <option value="PHOTO_CAPTURE_CANCELLED">Photo Capture Cancelled</option>
                                                <option value="UPLOAD_START">Upload Start</option>
                                                <option value="UPLOAD_SUCCESS">Upload Success</option>
                                                <option value="UPLOAD_ERROR">Upload Error</option>
                                                <option value="UPLOAD_TIMEOUT">Upload Timeout</option>
                                                <option value="UPLOAD_NETWORK_ERROR">Upload Network Error</option>
                                                <option value="UPLOAD_AUTH_EXPIRED">Upload Auth Expired</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Grid.Col>
                                    <Grid.Col md={6} className="d-flex align-items-end">
                                        <Button.List>
                                            <Button color="primary" onClick={fetchLogs} disabled={loading}>
                                                {loading ? 'Loading...' : 'Refresh'}
                                            </Button>
                                            <Button color="success" onClick={downloadLogs} disabled={logs.length === 0}>
                                                Download JSON
                                            </Button>
                                            <Button color="danger" onClick={deleteAllLogs} disabled={logs.length === 0}>
                                                Delete All
                                            </Button>
                                            <Form.Checkbox
                                                label="Auto-refresh (3s)"
                                                checked={autoRefresh}
                                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                            />
                                        </Button.List>
                                    </Grid.Col>
                                </Grid.Row>
                            </Card.Body>
                        </Card>
                    </Grid.Col>
                </Grid.Row>

                <div style={{height: '20px'}}></div>

                {Object.keys(consolidatedSessions).map((sessionId) => (
                    <div key={sessionId} style={{marginBottom: '20px'}}>
                    <Grid.Row>
                        <Grid.Col md={12}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>
                                        Session: {sessionId}
                                        <Badge className="ml-2" color="secondary">
                                            {consolidatedSessions[sessionId].length} events
                                        </Badge>
                                        {consolidatedSessions[sessionId][0]?.user && (
                                            <Badge className="ml-2" color="primary">
                                                {consolidatedSessions[sessionId][0].user.email}
                                            </Badge>
                                        )}
                                    </Card.Title>
                                </Card.Header>
                                <Table>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColHeader>Timestamp</Table.ColHeader>
                                            <Table.ColHeader>Event Type</Table.ColHeader>
                                            <Table.ColHeader>User</Table.ColHeader>
                                            <Table.ColHeader>Details</Table.ColHeader>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {consolidatedSessions[sessionId].map((log) => (
                                            <Table.Row key={log.id}>
                                                <Table.Col>
                                                    <Text size="sm" muted>
                                                        {new Date(log.event_timestamp).toLocaleString()}
                                                    </Text>
                                                </Table.Col>
                                                <Table.Col>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 8px',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: '#fff',
                                                        backgroundColor: getEventColor(log.event_type || 'UNKNOWN') === 'success' ? '#5eba00' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'danger' ? '#cd201f' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'warning' ? '#f1c40f' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'info' ? '#45aaf2' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'secondary' ? '#868e96' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'purple' ? '#6f42c1' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'orange' ? '#fd7e14' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'green' ? '#28a745' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'red' ? '#dc3545' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'blue' ? '#007bff' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'cyan' ? '#17a2b8' :
                                                                       getEventColor(log.event_type || 'UNKNOWN') === 'azure' ? '#45aaf2' :
                                                                       '#467fcf'
                                                    }}>
                                                        {log.event_type ? getEventLabel(log.event_type) : 'Unknown Event'}
                                                    </span>
                                                    {!log.event_type && (
                                                        <Text size="sm" muted style={{display: 'block', marginTop: '4px'}}>
                                                            (event_type is {JSON.stringify(log.event_type)})
                                                        </Text>
                                                    )}
                                                </Table.Col>
                                                <Table.Col>
                                                    {log.user ? (
                                                        <div>
                                                            <div>{log.user.name || log.user.email}</div>
                                                            <Text size="sm" muted>{log.user.email}</Text>
                                                        </div>
                                                    ) : (
                                                        <Text muted>N/A</Text>
                                                    )}
                                                </Table.Col>
                                                <Table.Col>
                                                    <details>
                                                        <summary style={{cursor: 'pointer', color: '#467fcf'}}>
                                                            {getSummaryText(log)}
                                                            {log.data?.stepIndex !== undefined && ` (Step ${log.data.stepIndex})`}
                                                        </summary>
                                                        <pre style={{fontSize: '11px', maxHeight: '300px', overflow: 'auto', marginTop: '8px', padding: '8px', background: '#f5f7fb', borderRadius: '3px'}}>
                                                            {log.isGroup
                                                                ? JSON.stringify(log.data.photos, null, 2)
                                                                : JSON.stringify(log.data, null, 2)
                                                            }
                                                        </pre>
                                                    </details>
                                                </Table.Col>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </Card>
                        </Grid.Col>
                    </Grid.Row>
                    </div>
                ))}

                {logs.length === 0 && !loading && (
                    <Grid.Row>
                        <Grid.Col md={12}>
                            <Card>
                                <Card.Body className="text-center">
                                    <Text.Small muted>
                                        <h5>No logs found</h5>
                                        <p>Start a measurement session in the mobile app to see logs appear here</p>
                                    </Text.Small>
                                </Card.Body>
                            </Card>
                        </Grid.Col>
                    </Grid.Row>
                )}
            </Page.Content>
        </SiteWrapper>
    );
}

export default MeasureLogs;
